<template>
  <v-list
    dense
    nav
  >
    <template
      v-for="item in menuItems"
    >
      <!--      PARENT MENU - EXPANDABLE -->
      <template
        v-if="hasChildren(item)"
      >
        <v-list-group
          :key="getItemKey(item)"
          v-model="openGroups[getItemKey(item)]"
          class="v-drawer-text-style"
          append-icon=""
          :class="{
            'child-list-item': isDashedMenu(item),
            'mark-root-menu': !isDashedMenu(item) && isChildItemActive(item)
          }"
          no-action
        >
          <!--          menu item icon-->
          <template
            #prependIcon
          >
            <v-list-item-icon
              :class="{
                'nested-item': isDashedMenu(item)
              }"
            >
              <image-field
                :src="getMenuIcon(item)"
                contain
                max-width="16"
                height="16"
              />
              <v-badge
                v-if="hasUnviewedReportsInChildren(item) && unviewedReportsCount"
                color="red"
                :content="unviewedReportsCount"
                overlap
              />
            </v-list-item-icon>
          </template>

          <!--          menu item title-->
          <template
            #activator
          >
            <v-list-item-title
              class="v-drawer-text-style d-flex align-center"
              :class="{
                'child-list-item-title': isDashedMenu(item),
                'unset-child-list-item-title-style': !isChildItemActive(item)
              }"
            >
              {{ item.name }}
              <v-spacer />
              <v-icon
                class="v-drawer-icon-color"
              >
                mdi-chevron-down
              </v-icon>
            </v-list-item-title>
          </template>

          <nested-menu
            :menu-items="item.children"
            :base-route="baseRoute || item.route"
          />
        </v-list-group>
      </template>

      <!--      NESTED MENU - CHILD -->
      <template
        v-else
      >
        <v-list-item
          :key="getItemKey(item)"
          :exact="item.route === '/' || hasChildren(item)"
          :class="{
            'v-drawer-text-style drawer-list-item': true,
            'child-list-item': isDashedMenu(item),
            'mark-root-menu': !isDashedMenu(item) && isChildItemActive(item),
            'menu-item--not-active': !isChildItemActive(item)
          }"
          color="white"
          :to="{
            name: item.vue_name
          }"
          link
          @click="navigationDrawerItemClicked(item)"
        >
          <v-list-item-icon
            :class="{
              'nested-item': isDashedMenu(item)
            }"
          >
            <image-field
              :src="getMenuIcon(item)"
              contain
              max-width="16"
              height="16"
            />
            <v-badge
              v-if="item.vue_name === 'Home' && unreadNewsCount"
              color="red"
              :content="unreadNewsCount"
              overlap
            />
            <v-badge
              v-if="item.vue_name === 'GeneratedReports' && unviewedReportsCount"
              color="red"
              :content="unviewedReportsCount"
              overlap
            />
          </v-list-item-icon>

          <v-list-item-title
            class="v-drawer-text-style"
            :class="{
              'v-drawer-text-style': true,
              'child-list-item-title': isDashedMenu(item),
              'child-title-padding-left': item.icon && item.icon !== 'dashed',
              'unset-child-list-item-title-style': !isChildItemActive(item)
            }"
          >
            {{ item.name }}
          </v-list-item-title>
        </v-list-item>
      </template>
    </template>
  </v-list>
</template>

<script>
import isEmpty from 'lodash/isEmpty'
import { createNamespacedHelpers } from 'vuex'
import ImageField from '@/global/components/fields/ImageField.vue'
const { mapGetters, mapActions } = createNamespacedHelpers('base/config')

export default {
  name: 'NestedMenu',

  components: {
    ImageField,
    NestedMenu: this
  },

  props: {
    menuItems: {
      type: Array,
      default: () => ([])
    },
    baseRoute: {
      type: String,
      default: ''
    }
  },

  data () {
    return {
      openGroups: {}
    }
  },

  computed: {
    ...mapGetters({
      menu: 'menu',
      user: 'user',
      unviewedReportsCount: 'unviewedReportsCount'
    }),

    unreadNewsCount () {
      return this.user?.unread_news?.unread_news_count || 0
    },

    // Computed off the whole menu, not off this instance's `menuItems`: picking the
    // most specific match has to compare against every leaf in the drawer, and
    // every nested instance renders only its own slice of it.
    bestMatchLength () {
      return this.findBestMatchLength(this.menu, '')
    }
  },

  watch: {
    $route: {
      immediate: true,
      handler () {
        this.menuItems.forEach(item => {
          if (this.hasChildren(item)) {
            this.$set(this.openGroups, this.getItemKey(item), this.isChildItemActive(item))
          }
        })
      }
    }
  },

  methods: {
    ...mapActions({
      resetUserReadNewsCount: 'resetUserReadNewsCount'
    }),

    getItemKey (item) {
      const { id, route, vue_name: vueName } = item || {}
      return id ?? `${route}-${vueName}`
    },

    getMenuIcon (item) {
      const { icon } = item || {}
      if (!icon || icon === 'dashed') return

      const isActive = this.isChildItemActive(item)

      return isActive
        ? `/img/icons/${icon}-white.svg`
        : `/img/icons/${icon}.svg`
    },

    // Turns a stored route into a leading-slash path with no trailing slash.
    normalizeRoute (route) {
      if (!route) {
        return null
      }

      const trimmedRoute = route.replace(/^\/+|\/+$/g, '')
      return trimmedRoute ? `/${trimmedRoute}` : '/'
    },

    // Resolves a menu item's own `route` against the base route it is nested
    // under, into the absolute path it points at.
    resolveFullRoute (route, baseRoute) {
      const normalizedRoute = this.normalizeRoute(route)

      if (!normalizedRoute || normalizedRoute === '/') {
        return normalizedRoute
      }

      // The base route has to be normalized too, not used raw: the seeders are not
      // consistent about leading slashes (the base module stores parents as
      // 'trackers'/'reports' but their children as '/tracker-models'), so comparing
      // and concatenating the raw values produced paths like
      // 'trackers/tracker-models' - no leading slash, matching nothing.
      const normalizedBaseRoute = this.normalizeRoute(baseRoute)

      if (!normalizedBaseRoute || normalizedBaseRoute === '/') {
        return normalizedRoute
      }

      return normalizedRoute.startsWith(`${normalizedBaseRoute}/`) || normalizedRoute === normalizedBaseRoute
        ? normalizedRoute
        : normalizedBaseRoute + normalizedRoute
    },

    /**
     * The path a leaf actually navigates to. Preferred over its stored `route`,
     * because the two disagree in several modules - the menu stores '/sim' and
     * '/send' while the router serves '/sims/sim-cards' and '/sms/send-sms' - and it
     * is the router's path that $route.path is compared against. `vue_name` is the
     * same name the template already navigates by, so this cannot drift.
     */
    resolveLeafPath (item, baseRoute) {
      const { vue_name: vueName } = item || {}

      if (vueName && this.$router) {
        try {
          const { route } = this.$router.resolve({ name: vueName }) || {}

          // `matched` is what says the name resolved to a real record. Vue Router
          // does NOT throw on an unknown name - it returns a route with path '/'.
          // Menus routinely point at screens a build does not have yet, and without
          // this check every one of them claimed to live at '/', which opened their
          // whole parent chain as soon as you navigated to the home screen.
          //
          // The `:` guard covers a named route carrying params (a details screen),
          // which resolves with its placeholders unfilled.
          if (route?.matched?.length && route.path && !route.path.includes(':')) {
            return this.normalizeRoute(route.path)
          }
        }
        catch (exception) {
          // Fall through to the stored route.
        }
      }

      return this.resolveFullRoute(item?.route, baseRoute)
    },

    routeMatchesCurrentPath (fullRoute) {
      if (!fullRoute) {
        return false
      }

      const currentPath = this.$route.path || '/'

      if (fullRoute === '/') {
        return currentPath === '/'
      }

      return currentPath === fullRoute || currentPath.startsWith(`${fullRoute}/`)
    },

    /**
     * Length of the most specific leaf route that matches the current path.
     * Prefix matching is what keeps a leaf lit on its own detail pages
     * (/subcontractors/12 belongs to /subcontractors), but on its own it also lit
     * every ancestor leaf - standing on /users/activity marked both "Radnje" and
     * "Lista" (/users) active. Only the longest match wins.
     */
    findBestMatchLength (items, baseRoute) {
      let bestLength = 0

      ;(items || []).forEach(item => {
        if (this.hasChildren(item)) {
          // Mirrors the `base-route` the template hands to the nested menu.
          bestLength = Math.max(
            bestLength,
            this.findBestMatchLength(item.children, baseRoute || item.route)
          )
          return
        }

        const fullRoute = this.resolveLeafPath(item, baseRoute)
        if (this.routeMatchesCurrentPath(fullRoute)) {
          bestLength = Math.max(bestLength, fullRoute.length)
        }
      })

      return bestLength
    },

    isChildItemActive (item, baseRoute = this.baseRoute) {
      if (!item) {
        return false
      }

      if (!this.hasChildren(item)) {
        const leafPath = this.resolveLeafPath(item, baseRoute)
        return this.routeMatchesCurrentPath(leafPath) && leafPath.length === this.bestMatchLength
      }

      // A group is active when one of its own descendants is - NOT when its route
      // merely prefixes the current path. Prefix matching opened every group whose
      // path is an ancestor of another's, so "Winter service"
      // (/road-maintenance/winter-service) also opened for every
      // /road-maintenance/winter-service/v2/... route. Its own route is still
      // matched, but exactly, for groups that are a page in their own right.
      const groupRoute = this.resolveFullRoute(item.route, baseRoute)
      if (groupRoute && (this.$route.path || '/') === groupRoute) {
        return true
      }

      // Mirrors the `base-route` the template hands to the nested menu.
      return item.children.some(child => this.isChildItemActive(child, baseRoute || item.route))
    },

    isDashedMenu (item) {
      const { vue_name: vueName, icon } = item || {}
      return (vueName && !icon) || icon === 'dashed'
    },

    navigationDrawerItemClicked (item) {
      if (item.route === '/') {
        this.resetUserReadNewsCount()
      }
      this.childItemClicked(item)
    },

    hasChildren (item) {
      return !isEmpty(item.children)
    },

    hasUnviewedReportsInChildren (item) {
      if (!item.children) return false
      return item.children.some(child =>
        child.vue_name === 'GeneratedReports' || this.hasUnviewedReportsInChildren(child)
      )
    },

    childItemClicked (child) {
      if (child.vue_name === this.$route.name) {
        return
      }

      this.toggleMenuNav()
    },

    toggleMenuNav () {
      if (this.$store.state.isMenuNavOpen) {
        setTimeout(() => this.$store.commit('toggleMenuNav', false), 80)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.v-drawer-text-style {
  font-family: 'Satoshi', sans-serif;
  color: #65778C;
  font-size: 13px !important;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: 0;
}

.v-drawer-icon-color {
  color: #65778C;
}

.v-list-item--active .v-drawer-text-style, .v-list-item--active .v-drawer-icon-color {
  color: white !important;
}

/* Vuetify's `v-list-item--active` comes from router-link, which matches
   INCLUSIVELY - standing on /users/activity also marks /users ("Lista") active, so
   it kept the white label and dot while everything driven by isChildItemActive had
   already dropped them. Same specificity as the rules above, declared after, so it
   wins for the leaf we know is not the most specific match. */
.menu-item--not-active .v-drawer-text-style, .menu-item--not-active .v-drawer-icon-color {
  color: #65778C !important;
}

.nested-item {
  position: relative;
  content: "";
  border-left: 1px dashed rgba(255, 255, 255, 0.3);
  left: 0;
  margin-top: 0 !important;
  margin-bottom: 0 !important;
  height: 40px !important;
}

.nested-item::after {
  content: "";
  position: absolute;
  top: 50%;
  left: 0;
  transform: translate(-50%, -50%);
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #65778C;
  z-index: 2;
}

.v-list-item--active .nested-item::after {
  background: #ffffff;
}

/* Same reason as the label colour above - router-link's inclusive match left the
   dot white on an ancestor leaf. */
.menu-item--not-active .nested-item::after {
  background: #65778C;
}

::v-deep {
  .v-list-item__icon {
    margin-top: 0 !important;
    margin-bottom: 0 !important;
  }

  .v-list-item {
    margin-bottom: 0 !important;
  }

  .v-list-item__icon:first-child {
    margin-right: 6px !important;
    align-self: center;
    align-items: center;
  }

  .v-list-item__icon.nested-item {
    margin-right: 0 !important;
  }

  .v-list-group__header__prepend-icon:has(.nested-item) {
    margin-right: 0 !important;
    align-self: center;
    align-items: center;
  }

  .child-list-item-title {
    padding: 12px 10px;
  }

  .v-list-item--active .child-list-item-title {
    background: #111827;
    border: 1px solid #374151;
    border-radius: 8px;
  }

  .unset-child-list-item-title-style {
    background: unset !important;
    border: unset !important;
  }

  .child-list-item {
    padding-right: 0;
    border-left: unset !important;
  }

  .child-title-padding-left {
    padding-left: 4px;
  }
}

.drawer-list-item::before {
  background: unset !important;
}

.v-navigation-drawer > .v-navigation-drawer__content > .v-list {
  padding: 20px;
}

::v-deep.v-list > .v-list-group.mark-root-menu > .v-list-item {
  position: relative;
}

::v-deep.v-list > .v-list-group.mark-root-menu > .v-list-item::after {
  content: "";
  position: absolute;
  left: -20px;          /* SAME as v-list padding */
  top: 0;
  bottom: 0;
  width: 4px;
  background: #fff;
  border-left: 2px solid #fff;
  border-radius: 0;
}

::v-deep.v-list > .v-list-item.mark-root-menu:after {
  content: "";
  position: absolute;
  left: -20px;          /* SAME as v-list padding */
  top: 0;
  bottom: 0;
  width: 4px;
  background: #fff;
  border-left: 2px solid #fff;
  border-radius: 0;
}
</style>
